/**
 * WalkInConfirmationPage.js (Phase 1 — 2026-09-25)
 *
 * Where Stripe sends a walk-in customer's browser after they pay.
 *
 * Why this page exists: the walk-in checkout session has always set a `success_url`,
 * but no such route was ever registered, so a customer who had just paid landed on a
 * BLANK page. The booking itself was fine (the webhook confirms it, the desk sees
 * "Paid") — only the customer saw nothing.
 *
 * Public on purpose: the customer is usually a guest with no session.
 *
 * Reads `GET /api/bookings/walk-in/status?session_id=...`, which is read-only. It
 * deliberately does NOT call GET /api/bookings/confirm, because that endpoint runs
 * conflict detection and can move the booking — a receipt must never modify what it
 * is describing.
 *
 * Styling reuses BookingConfirmation.css, whose class names are the ones the sibling
 * SlotConflictPage already uses. Do not invent new ones — a class that does not exist
 * renders an unstyled page.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import "./BookingConfirmation.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// The webhook can land a second or two after Stripe redirects the browser here, so
// poll briefly rather than telling a paying customer we cannot find their booking.
const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 60000;

const formatLongDate = (ymd) => {
  if (!ymd) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(new Date(`${ymd}T12:00:00`));
  } catch {
    return String(ymd);
  }
};

const formatClock = (value) => {
  if (!value) return "";
  const [hours, minutes] = String(value).split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return String(value);
  const period = hours >= 12 ? "PM" : "AM";
  return `${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${period}`;
};

const formatMoney = (amount, currency) => {
  const value = Number(amount) || 0;
  const symbol = currency === "EUR" ? "€" : currency === "GBP" ? "£" : "$";
  return `${symbol}${value.toFixed(2)}`;
};

function WalkInConfirmationPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [booking, setBooking] = useState(null);
  // loading | paid | pending | released | notfound | invalid
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const [proposal, setProposal] = useState(null);
  const [answering, setAnswering] = useState(false);
  const [answerError, setAnswerError] = useState("");

  const pollStartRef = useRef(0);
  const pollTimerRef = useRef(null);
  // While a replacement time is on offer, keep polling: the desk can answer for the
  // customer, and this page should pick that up instead of timing out.
  const proposalPendingRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setState("invalid");
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/bookings/walk-in/status?session_id=${encodeURIComponent(
            sessionId,
          )}`,
        );

        if (res.status === 404) {
          if (!cancelled) setState("notfound");
          stopPolling();
          return;
        }
        if (!res.ok) throw new Error("status_failed");

        const data = await res.json();
        if (cancelled) return;

        setBooking(data);

        const paid =
          data.paymentStatus === "paid" ||
          data.status === "confirmed" ||
          data.status === "completed";

        if (paid) {
          setState("paid");
          stopPolling();
          return;
        }

        // Paid, but the time was taken. The desk is asked the same question, and
        // whichever screen answers first wins - the other updates on its next poll.
        if (data.canRespondToProposal && data.proposal) {
          proposalPendingRef.current = true;
          setProposal(data.proposal);
          setState("proposal");
          // Keep polling: the desk may answer for the customer.
          return;
        }

        if (data.status === "cancelled") {
          // The hold lapsed and the sweep released it before payment landed.
          setState("released");
          stopPolling();
          return;
        }

        // Paid, but the booked time was taken and there is NOTHING free left today, so
        // there is no alternative to offer. The desk is choosing the time (they and the
        // professional are together on site), so all this screen does is say that a time
        // is being arranged. No email or text is sent for a walk-in move.
        if (data.awaitingPlacement) {
          proposalPendingRef.current = true;
          setProposal(null);
          setState("arranging");
          // Keep polling: the desk places it and this screen then shows the real
          // confirmation, with the final time.
          return;
        }

        // Still pending: keep polling while the webhook catches up.
        setState("pending");
        if (!proposalPendingRef.current && Date.now() - pollStartRef.current > POLL_TIMEOUT_MS) {
          stopPolling();
        }
      } catch (err) {
        if (!cancelled) setError("We could not check your payment just now.");
      }
    };

    pollStartRef.current = Date.now();
    load();
    pollTimerRef.current = setInterval(load, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [sessionId, stopPolling]);

  /** Answer the offered time. Same endpoint the desk's modal uses. */
  const answerProposal = async (accept) => {
    if (!sessionId) return;
    setAnswering(true);
    setAnswerError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/bookings/walk-in/proposal-response`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, accept }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // 409 means the desk already answered. Not an error - just re-read the state.
        if (res.status === 409) {
          proposalPendingRef.current = false;
          setProposal(null);
          return;
        }
        throw new Error(data.message || "Unable to record your answer");
      }

      proposalPendingRef.current = false;
      setProposal(null);

      if (accept) {
        setState("paid");
        stopPolling();
      } else {
        setState("released");
        stopPolling();
      }
    } catch (err) {
      setAnswerError(err.message || "Unable to record your answer");
    } finally {
      setAnswering(false);
    }
  };

  const details = booking    ? [
        { label: "Service", value: booking.serviceName },
        { label: "Date", value: formatLongDate(booking.appointmentDate) },
        {
          label: "Time",
          value: `${formatClock(booking.startTime)}${
            booking.endTime ? ` - ${formatClock(booking.endTime)}` : ""
          }`,
        },
        {
          label: "Location",
          value:
            [booking.shopName, booking.shopCity, booking.shopState]
              .filter(Boolean)
              .join(", ") || null,
        },
        {
          label: "Amount paid",
          // The amount actually collected for this checkout. A group's primary
          // booking carries only its OWN share in `totalAmount`, so using that here
          // showed $74.44 for a card charged $94.44. `amountPaid` is set from
          // Stripe's own amount (or the cash the desk took); the fallback covers
          // rows written before that field existed.
          value: formatMoney(
            booking.amountPaid ?? booking.totalAmount,
            booking.currency,
          ),
        },
        // Shown only when there IS tax - a "Sales Tax $0.00" row on every service-only
        // walk-in would be noise. Services are not taxed; products are, and Stripe
        // calculates it from the buyer's address (the same figure the online
        // confirmation page reads).
        ...(Number(booking.taxAmount) > 0
          ? [
              {
                label: "Sales tax",
                value: formatMoney(booking.taxAmount, booking.currency),
              },
            ]
          : []),
      ].filter((row) => row.value)
    : [];

  if (state === "loading") {
    return (
      <>
        <CustomerHeader />
        <div className="confirmation-container">
          <div className="confirmation-loading">
            <div className="spinner" />
            <p>Checking your payment…</p>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  if (state === "proposal" && proposal) {    return (
      <>
        <CustomerHeader />
        <div className="confirmation-container">
          <div className="confirmation-success">
            <div className="confirmation-success-header">
              <div className="success-icon">🗓️</div>
              <h1>That time was taken</h1>
              <p className="success-message">
                Your payment went through, but somebody else booked that slot first.
                We can move your appointment to the next available time below.
              </p>
            </div>

            <div className="booking-details">
              <div className="detail-section">
                <h2>Next available</h2>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">Date</span>
                    <span className="value">{formatLongDate(proposal.proposedDate)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Time</span>
                    <span className="value">
                      {formatClock(proposal.proposedStartTime)}
                      {proposal.proposedEndTime
                        ? ` - ${formatClock(proposal.proposedEndTime)}`
                        : ""}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="confirmation-note">
              <p>
                If this does not work, decline and the full amount is refunded — just
                ask the desk.
              </p>
            </div>

            {answerError ? <p>{answerError}</p> : null}

            <div className="confirmation-actions">
              <button
                type="button"
                className="btn-secondary"
                disabled={answering}
                onClick={() => answerProposal(false)}
              >
                Decline &amp; refund
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={answering}
                onClick={() => answerProposal(true)}
              >
                {answering ? "Saving…" : "Accept this time"}
              </button>
            </div>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  // Paid, but the booked time was taken AND nothing else is free today, so the desk is
  // choosing the time. Deliberately NOT the "confirming your payment" screen: the money
  // is in, and the only thing outstanding is the time. No email or text is sent for a
  // walk-in move, so THIS PAGE IS THE NOTIFICATION - which means it must keep polling
  // and then turn into the real confirmation, with the final time, once the desk places
  // it.
  if (state === "arranging") {
    return (
      <>
        <CustomerHeader />
        <div className="confirmation-container">
          <div className="confirmation-success">
            <div className="confirmation-success-header">
              <div className="success-icon">✓</div>
              <h1>Payment received</h1>
              <p className="success-message">
                Your payment went through, but somebody else took that time and there is
                nothing else free today. The desk is finding an appropriate time for you.
              </p>
            </div>

            <div className="confirmation-note">
              <p>
                Keep this page open — it updates on its own and will show your
                appointment as soon as the new time is confirmed. Please stay with the
                desk.
              </p>
            </div>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  const problems = {
    invalid: {
      icon: "⚠️",
      title: "This link is incomplete",
      message:
        "The page needs the payment reference from your receipt. Please ask the desk to confirm your appointment.",
    },
    notfound: {
      icon: "⚠️",
      title: "We couldn't find that payment",
      message:
        "If money has left your account, show this screen to the desk — the payment can be traced from your receipt.",
    },
    released: {
      icon: "⚠️",
      title: "This walk-in was released",
      message:
        "The time was held briefly and released before the payment completed. Nothing has been charged — please ask the desk to start it again.",
    },
  };

  if (problems[state]) {
    return (
      <>
        <CustomerHeader />
        <div className="confirmation-container">
          <div className="confirmation-error">
            <div className="error-icon">{problems[state].icon}</div>
            <h1>{problems[state].title}</h1>
            <p>{problems[state].message}</p>
            {error ? <p>{error}</p> : null}
            <Link className="btn-primary" to="/">
              Done
            </Link>
          </div>
        </div>
        <CustomerFooter />
      </>
    );
  }

  return (
    <>
      <CustomerHeader />
      <div className="confirmation-container">
        <div className="confirmation-success">
          <div className="confirmation-success-header">
            <div className="success-icon">{state === "paid" ? "✓" : "⏳"}</div>
            <h1>
              {state === "paid" ? "Payment received" : "Confirming your payment…"}
            </h1>
            <p className="success-message">
              {state === "paid"
                ? "Your appointment is confirmed. Please stay seated — the desk can see it."
                : "This can take a few seconds. Keep this page open, or show it to the desk if it does not settle."}
            </p>
          </div>

          {booking?.movedFrom ? (
            <div className="time-change-notice">
              <div className="time-change-head">
                <span className="time-change-icon" aria-hidden="true">
                  ⟳
                </span>
                <div>
                  <strong>Your time changed</strong>
                  <span>
                    The slot you booked was taken while your payment was going through,
                    so we moved you automatically.
                  </span>
                </div>
              </div>
              <div className="time-change-compare">
                <div className="time-change-col was">
                  <span className="time-change-label">You booked</span>
                  <span className="time-change-value">
                    {formatClock(booking.movedFrom) || "—"}
                  </span>
                  {booking.movedFromDate &&
                  booking.movedFromDate !== booking.appointmentDate ? (
                    <span className="time-change-date">
                      {formatLongDate(booking.movedFromDate)}
                    </span>
                  ) : null}
                </div>
                <span className="time-change-arrow" aria-hidden="true">
                  →
                </span>
                <div className="time-change-col now">
                  <span className="time-change-label">Your new time</span>
                  <span className="time-change-value">
                    {formatClock(booking.startTime) || "—"}
                    {booking.endTime ? ` – ${formatClock(booking.endTime)}` : ""}
                  </span>
                </div>
              </div>
            </div>
          ) : null}

          {details.length > 0 ? (
            <div className="booking-details">
              <div className="detail-section">
                <h2>Your appointment</h2>
                <div className="detail-grid">
                  {details.map((row) => (
                    <div key={row.label} className="detail-item">
                      <span className="label">{row.label}</span>
                      <span className="value">{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          <div className="confirmation-actions">
            <Link className="btn-primary" to="/">
              Done
            </Link>
          </div>
        </div>
      </div>
      <CustomerFooter />
    </>
  );
}

export default WalkInConfirmationPage;
